/**
 * fhirSyncService.js — Servicio de Reconciliación Clínica con Google Cloud Healthcare API (FHIR)
 *
 * Fuente Única de Verdad: Google Cloud Healthcare API (FHIR Store en GCP)
 * Firestore: Caché reactivo optimista de lectura rápida y modo offline.
 *
 * Este servicio:
 * 1. Consulta el Bundle FHIR del paciente (/api/patients/everything) o estado (/api/onboarding/state).
 * 2. Normaliza observaciones LOINC a variables clínicas universales de WE360.
 * 3. Evalúa la coherencia temporal (las observaciones oficiales de FHIR mandan sobre datos locales).
 * 4. Aplica las reglas biomecánicas vigentes (ej: WHtR Cintura/Altura > 0.5).
 */

import { OBSERVATION_DEFS } from "@/data/loinc";
import { getCoreServicesConfig } from "@/services/coreServicesClient";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;

function getGcpHeaders() {
  try {
    const config = getCoreServicesConfig();
    const targetUrl = config?.apiUrl || config?.url;
    return {
      "Content-Type": "application/json",
      ...(targetUrl ? { "x-core-target-url": targetUrl } : {}),
      ...(config?.apiKey ? { "x-core-api-key": config.apiKey } : {})
    };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

// Mapa inverso: LOINC Code -> Nombre de campo en el perfil
const LOINC_TO_FIELD = {};
for (const [field, def] of Object.entries(OBSERVATION_DEFS)) {
  if (def.code) {
    LOINC_TO_FIELD[def.code] = field;
  }
}
export const LOINC_TO_PROFILE_MAP = LOINC_TO_FIELD;

/**
 * Consulta el Bundle FHIR completo del paciente desde Google Cloud Healthcare API
 * a través de nuestro proxy seguro hacia el backend de GCP.
 *
 * @param {string} email - Correo del usuario registrado.
 * @returns {Promise<{ ok: boolean, resources: Array, error?: string }>}
 */
export async function fetchFhirBundle(email) {
  if (!email) return { ok: false, resources: [] };
  try {
    const endpoint = encodeURIComponent("/api/patients/everything");
    const url = `${BACKEND_URL}/api/gcp/query?endpoint=${endpoint}&email=${encodeURIComponent(email)}`;
    const res = await fetch(url, { headers: getGcpHeaders() });
    if (!res.ok) {
      // Fallback a historial si everything aún no está disponible para este paciente
      const historyEndpoint = encodeURIComponent("/api/patients/history");
      const histUrl = `${BACKEND_URL}/api/gcp/query?endpoint=${historyEndpoint}&email=${encodeURIComponent(email)}`;
      const histRes = await fetch(histUrl, { headers: getGcpHeaders() });
      if (!histRes.ok) {
        return { ok: false, resources: [], status: histRes.status };
      }
      const histData = await histRes.json();
      return { ok: true, resources: extractResourcesFromPayload(histData), raw: histData };
    }
    const data = await res.json();
    const isError = data?.status === "Error" || data?.message === "Patient not found.";
    const patientFound = !isError && !!(data?.profile || data?.resourceType === "Bundle" || Array.isArray(data));
    return {
      ok: !isError,
      patientFound,
      resources: isError ? [] : extractResourcesFromPayload(data),
      raw: data,
    };
  } catch (err) {
    console.warn("[FHIR Sync] Fallo al consultar Google Healthcare API:", err.message);
    return { ok: false, patientFound: false, resources: [], raw: null, error: err.message };
  }
}

/**
 * Extrae un arreglo plano de recursos FHIR desde diferentes estructuras de respuesta
 * (Bundle estándar FHIR R4, searchset, o listas de entidades).
 */
function extractResourcesFromPayload(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.resourceType === "Bundle" && Array.isArray(data.entry)) {
    return data.entry.map(e => e.resource).filter(Boolean);
  }
  // Estructura oficial devuelta por GCP /api/patients/everything
  if (data.profile) {
    const list = [];
    if (data.profile.patient) list.push(data.profile.patient);
    if (Array.isArray(data.profile.observations)) list.push(...data.profile.observations);
    if (Array.isArray(data.profile.conditions)) list.push(...data.profile.conditions);
    if (Array.isArray(data.profile.medications)) list.push(...data.profile.medications);
    if (Array.isArray(data.profile.other)) list.push(...data.profile.other);
    return list;
  }
  if (Array.isArray(data.observations)) {
    return data.observations;
  }
  if (Array.isArray(data.metrics)) {
    return data.metrics;
  }
  return [];
}

/**
 * Consulta el estado de clasificación y reconciliación FHIR desde GCP.
 *
 * @param {{ phone?: string, email?: string }} identifier
 * @returns {Promise<{ ok: boolean, state?: Object, error?: string }>}
 */
export async function fetchOnboardingState({ phone, email }) {
  if (!phone && !email) return { ok: false };
  try {
    const param = phone ? `phone=${encodeURIComponent(phone)}` : `email=${encodeURIComponent(email)}`;
    const url = `${BACKEND_URL}/api/gcp/onboarding-state?${param}`;
    const res = await fetch(url, { headers: getGcpHeaders() });
    if (!res.ok) return { ok: false, status: res.status };
    const data = await res.json();
    if (data?.status === "Error") {
      return { ok: false, error: data.message };
    }
    const state = data?.state || data;
    return { ok: true, state };
  } catch (err) {
    console.warn("[FHIR Sync] Error consultando onboarding-state:", err.message);
    return { ok: false, error: err.message };
  }
}

/**
 * Extrae código, sistema y descripción desde un nodo FHIR (`res` o `component`).
 */
function extractCodingMeta(node) {
  let code = null;
  let display = node?.display || "";
  let system = node?.system || "http://loinc.org";

  if (node?.code?.coding && Array.isArray(node.code.coding)) {
    const coding = node.code.coding.find((c) => c.system === "http://loinc.org") || node.code.coding[0];
    code = coding?.code;
    display = coding?.display || node.code.text || display;
    system = coding?.system || system;
  } else if (typeof node?.code === "string") {
    code = node.code;
  } else if (node?.display) {
    code = node.display;
  }

  return { code, display, system };
}

/**
 * Extrae el valor primitivo y unidad desde un nodo FHIR (`res` o `component`).
 */
function extractObservationValue(node) {
  let value = null;
  let unit = node?.unit;

  if (node?.valueQuantity !== undefined && node?.valueQuantity !== null) {
    value = node.valueQuantity.value;
    unit = node.valueQuantity.unit || unit;
  } else if (node?.value !== undefined && node?.value !== null) {
    value = node.value;
  } else if (node?.valueString !== undefined) {
    value = node.valueString;
  } else if (node?.valueInteger !== undefined) {
    value = node.valueInteger;
  } else if (node?.valueBoolean !== undefined) {
    value = node.valueBoolean;
  }

  return { value, unit };
}

/**
 * Normaliza las observaciones de Google Cloud Healthcare API agrupándolas
 * por código LOINC / WE360 y seleccionando el valor más reciente válido.
 * Soporta tanto recursos Observation simples como paneles compuestos (`component[]`,
 * ej. Presión Arterial 85354-9 con componentes 8480-6 y 8462-4 provenientes de WhatsApp u otros canales).
 *
 * @param {Array} resources - Lista de recursos extraídos del FHIR Bundle.
 * @returns {Record<string, { code: string, system?: string, value: any, unit?: string, effectiveDateTime?: string, display?: string }>}
 */
export function normalizeFhirObservations(resources = []) {
  const normalized = {};

  const upsertObs = (loincCode, system, value, unit, display, dateStr) => {
    if (!loincCode || loincCode === "undefined") return;
    if (value === null || value === undefined) return;
    if (typeof value === "number" && isNaN(value)) return;

    const existing = normalized[loincCode];
    const isNewer =
      !existing ||
      (dateStr && !existing.effectiveDateTime) ||
      (dateStr && existing.effectiveDateTime && new Date(dateStr) > new Date(existing.effectiveDateTime));

    if (isNewer) {
      normalized[loincCode] = {
        code: loincCode,
        system,
        value,
        unit,
        display: display || loincCode,
        effectiveDateTime: dateStr,
      };
    }
  };

  for (const res of resources) {
    if (!res) continue;
    const isObs =
      res.resourceType === "Observation" ||
      res.code ||
      Array.isArray(res.component) ||
      (res.display && res.value !== undefined);
    if (!isObs) continue;

    const dateStr =
      res.date || res.effectiveDateTime || res.issued || res.timestamp || res.createdAt || null;

    // 1. Si el recurso trae componentes hijos (ej. panel de Presión Arterial 85354-9 desde bot o dispositivo externo)
    if (Array.isArray(res.component) && res.component.length > 0) {
      for (const comp of res.component) {
        if (!comp) continue;
        const { code: compCode, display: compDisplay, system: compSystem } = extractCodingMeta(comp);
        const { value: compVal, unit: compUnit } = extractObservationValue(comp);
        upsertObs(compCode, compSystem, compVal, compUnit, compDisplay, dateStr);
      }
    }

    // 2. Extraer valor directo en la raíz de la Observation (si existe)
    const { code: loincCode, display, system } = extractCodingMeta(res);
    const { value, unit } = extractObservationValue(res);
    upsertObs(loincCode, system, value, unit, display, dateStr);
  }

  return normalized;
}

/**
 * Extrae datos demográficos e identidad desde un recurso `Patient` de FHIR
 * (útil cuando el usuario interactuó primero por el Bot de WhatsApp u otro canal antes de abrir la Web).
 *
 * @param {Array} resources - Lista de recursos extraídos del FHIR Bundle.
 * @returns {Object|null} Campos demográficos normalizados o null si no hay recurso Patient.
 */
export function extractPatientDemographicsFromFhir(resources = []) {
  const patient = (resources || []).find((r) => r && r.resourceType === "Patient");
  if (!patient) return null;

  let nombres = "";
  let apellidos = "";

  if (typeof patient.name === "string" && patient.name.trim()) {
    const parts = patient.name.trim().split(/\s+/);
    nombres = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
    apellidos = parts.length > 1 ? parts[parts.length - 1] : "";
  } else if (Array.isArray(patient.name) && patient.name.length > 0) {
    const humanName = patient.name[0];
    if (Array.isArray(humanName?.given)) {
      nombres = humanName.given.join(" ").trim();
    }
    if (humanName?.family) {
      apellidos = String(humanName.family).trim();
    }
    if (!nombres && !apellidos && humanName?.text) {
      const parts = String(humanName.text).trim().split(/\s+/);
      nombres = parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0];
      apellidos = parts.length > 1 ? parts[parts.length - 1] : "";
    }
  }

  const genderRaw = String(patient.gender || "").toLowerCase();
  const sexo =
    genderRaw === "male" ? "Masculino" : genderRaw === "female" ? "Femenino" : "";

  let phone = patient.whatsapp || patient.phone || "";
  if (!phone && Array.isArray(patient.telecom)) {
    const phoneEntry = patient.telecom.find((t) => t?.system === "phone" && t?.value);
    if (phoneEntry) phone = phoneEntry.value;
  }

  return {
    ...(patient.id && { fhirId: patient.id }),
    ...(nombres && { nombres }),
    ...(apellidos && { apellidos }),
    ...(patient.birthDate && { fechaNacimiento: patient.birthDate }),
    ...(sexo && { sexo }),
    ...(phone && { whatsapp: phone, telefono: phone }),
  };
}

/**
 * Extrae y deduplica los medicamentos activos (`MedicationStatement`) desde los recursos FHIR de GCP.
 * Soporta tanto el formato estructurado de `/api/patients/everything` (`display: "Nombre (Marca) - Dosis"`)
 * como recursos FHIR `MedicationStatement` nativos.
 *
 * @param {Array<Object>} resources - Lista de recursos FHIR.
 * @returns {Array<Object>} Lista de medicamentos normalizados para el perfil en memoria.
 */
export function extractMedicationsFromFhir(resources = []) {
  if (!Array.isArray(resources)) return [];
  const rawMeds = resources.filter(
    (r) => r && (r.resourceType === "MedicationStatement" || r.resourceType === "MedicationRequest")
  );
  if (rawMeds.length === 0) return [];

  const byKey = new Map();
  rawMeds.forEach((m) => {
    if (m.status && String(m.status).toLowerCase() === "stopped") return;
    const coding = m.medicationCodeableConcept?.coding?.[0] || {};
    const atc = m.code || m.atcCode || m.codigoATC || coding.code || "";
    let nombre = m.name || m.nombre || "";
    let nombreComercial = m.brandName || m.nombreComercial || "";
    let dosis = m.dosage || m.dosificacion || m.dosis || m.dosageInstruction?.[0]?.text || "";

    const rawDisplay = String(m.display || m.medicationCodeableConcept?.text || coding.display || "").trim();
    if (!nombre && rawDisplay) {
      const match = rawDisplay.match(/^(.+?)\s*\(([^)]*)\)\s*-\s*(.*)$/);
      if (match) {
        nombre = match[1].trim();
        const brandPart = match[2].trim();
        const dosagePart = match[3].trim();
        if (!nombreComercial && brandPart && brandPart !== "null" && brandPart !== "undefined") {
          nombreComercial = brandPart;
        }
        if (!dosis && dosagePart && dosagePart !== "null" && dosagePart !== "undefined") {
          dosis = dosagePart;
        }
      } else {
        nombre = rawDisplay
          .replace(/\s*\(\s*\)/g, "")
          .replace(/\s*-\s*(null|undefined)\s*$/i, "")
          .trim();
      }
    }

    if (!nombre) return;
    if (dosis === "null" || dosis === "undefined") dosis = "";
    if (nombreComercial === "null" || nombreComercial === "undefined") nombreComercial = "";

    const normalizedMed = {
      nombre,
      codigoATC: atc || null,
      dosis: dosis || "",
      dosificacion: dosis || "",
      nombreComercial: nombreComercial || "",
      source: m.source || (atc ? "catalog" : "manual"),
    };

    const dedupKey = atc ? `atc:${atc}` : `name:${nombre.toLowerCase()}`;
    byKey.set(dedupKey, normalizedMed);
  });

  return Array.from(byKey.values());
}

/**
 * Compara si dos valores clínicos (ej. "72" en memoria vs 72 en FHIR) son equivalentes
 * para evitar escrituras redundantes en Firestore.
 */
function areClinicalValuesEquivalent(valA, valB) {
  if (valA === valB) return true;
  if (valA === "" || valA === null || valA === undefined) return false;
  if (valB === "" || valB === null || valB === undefined) return false;
  const numA = Number(valA);
  const numB = Number(valB);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return Math.abs(numA - numB) < 0.0001;
  }
  return String(valA).trim() === String(valB).trim();
}

/**
 * Reconcilia el perfil en memoria (Firestore) con las observaciones de Google Healthcare API (FHIR).
 * Respeta el patrón Outbox y la coherencia temporal por métrica (`fhirFieldTimestamps`):
 * - Si hay cambios locales en la cola Outbox (`hasPendingLocalChanges` o `fhirSyncStatus === "PENDING_FHIR"`),
 *   FHIR solo rellena campos vacíos y NUNCA pisa mediciones locales pendientes de subir.
 * - Evalúa el timestamp individual de cada métrica (`fhirFieldTimestamps[fieldName]`, con fallback a
 *   `vitalsMeasurementDate`) frente a `obs.effectiveDateTime` para permitir que datos entrantes desde
 *   otros canales (ej. Peso o Glucemia vía WhatsApp) actualicen su campo sin chocar con un Face Scan web.
 *
 * @param {Object} currentProfile - Perfil en memoria de la web app.
 * @param {Record<string, Object>} normalizedObservations - Mapa de observaciones LOINC de FHIR.
 * @param {Object} [onboardingState] - Estado devuelto por GCP (clasificación de riesgo, fhirReconciled).
 * @param {{ hasPendingLocalChanges?: boolean, patientDemographics?: Object, fhirMedications?: Array<Object> }} [options] - Opciones de reconciliación.
 * @returns {{ reconciledProfile: Object, hasUpdates: boolean }} Perfil reconciliado con las fuentes oficiales de verdad.
 */
export function reconcileProfileWithFhir(
  currentProfile,
  normalizedObservations = {},
  onboardingState = null,
  options = {}
) {
  const updated = { ...currentProfile };
  const fieldTimestamps = { ...(currentProfile?.fhirFieldTimestamps || {}) };
  let hasUpdates = false;

  const hasPendingOutbox =
    Boolean(options?.hasPendingLocalChanges) ||
    currentProfile?.fhirSyncStatus === "PENDING_FHIR" ||
    currentProfile?.pendingFhirMetrics === true ||
    currentProfile?.pendingFhirF1 === true;

  const globalLocalTime = currentProfile?.vitalsMeasurementDate
    ? Date.parse(currentProfile.vitalsMeasurementDate)
    : NaN;

  // 0. Si vienen datos demográficos de FHIR (ej. usuario creado primero en WhatsApp), rellenar campos vacíos
  if (options?.patientDemographics && typeof options.patientDemographics === "object") {
    for (const [demoKey, demoVal] of Object.entries(options.patientDemographics)) {
      if (!demoVal) continue;
      const curDemoVal = updated[demoKey];
      if (curDemoVal === "" || curDemoVal === null || curDemoVal === undefined) {
        updated[demoKey] = demoVal;
        hasUpdates = true;
      }
    }
  }

  // 0.b Reconciliar medicamentos activos desde FHIR si el perfil local quedó vacío accidentalmente (0 FB / N GCP)
  // y el usuario no confirmó explícitamente que dejó de tomar medicamentos (medicationsConfirmedEmpty).
  if (
    Array.isArray(options?.fhirMedications) &&
    options.fhirMedications.length > 0 &&
    (!Array.isArray(updated.medicamentos) || updated.medicamentos.length === 0) &&
    !updated.medicationsConfirmedEmpty &&
    !hasPendingOutbox
  ) {
    updated.medicamentos = options.fhirMedications;
    updated.usaMedicamentos = "Si";
    hasUpdates = true;
  }

  let newestFhirTimeMs = Number.isNaN(globalLocalTime) ? 0 : globalLocalTime;
  let newestFhirDateIso = updated.vitalsMeasurementDate || null;

  // 1. Mapear observaciones LOINC oficiales a flat fields del perfil
  for (const [loincCode, obs] of Object.entries(normalizedObservations)) {
    const fieldName = LOINC_TO_FIELD[loincCode];
    if (!fieldName || !obs || obs.value === undefined || obs.value === null) continue;

    const currentVal = updated[fieldName];
    const isLocalEmpty = currentVal === "" || currentVal === undefined || currentVal === null;
    const fhirObsTime = obs.effectiveDateTime ? Date.parse(obs.effectiveDateTime) : NaN;

    // Timestamp específico del campo si existe; si no, fallback al timestamp global de medición
    const specificFieldDateStr = fieldTimestamps[fieldName];
    const localFieldTime = specificFieldDateStr
      ? Date.parse(specificFieldDateStr)
      : globalLocalTime;

    if (!isLocalEmpty) {
      // Protección 1: Si el usuario tiene mediciones pendientes en el Outbox, no pisar valores locales existentes
      if (hasPendingOutbox) {
        continue;
      }

      // Protección 2: Coherencia temporal — si el timestamp local de este campo es más reciente que el de FHIR, no regresar al pasado
      if (!Number.isNaN(localFieldTime) && !Number.isNaN(fhirObsTime) && localFieldTime > fhirObsTime) {
        continue;
      }

      // Protección 3: Si los valores son numéricamente idénticos ("72" vs 72), evitar re-escritura innecesaria
      if (areClinicalValuesEquivalent(currentVal, obs.value)) {
        if (obs.effectiveDateTime && !fieldTimestamps[fieldName]) {
          fieldTimestamps[fieldName] = obs.effectiveDateTime;
        }
        continue;
      }
    }

    updated[fieldName] = obs.value;
    if (obs.effectiveDateTime) {
      fieldTimestamps[fieldName] = obs.effectiveDateTime;
      if (!Number.isNaN(fhirObsTime) && fhirObsTime > newestFhirTimeMs) {
        newestFhirTimeMs = fhirObsTime;
        newestFhirDateIso = obs.effectiveDateTime;
      }
    }
    hasUpdates = true;
  }

  if (Object.keys(fieldTimestamps).length > 0) {
    updated.fhirFieldTimestamps = fieldTimestamps;
  }
  if (hasUpdates && newestFhirDateIso) {
    updated.vitalsMeasurementDate = newestFhirDateIso;
  }

  // 2. Cálculo estandarizado de Relación Cintura-Altura (WHtR > 0.5)
  const cintura = parseFloat(updated.cintura);
  const talla = parseFloat(updated.talla);
  if (!isNaN(cintura) && !isNaN(talla) && talla > 0) {
    const whtr = parseFloat((cintura / talla).toFixed(3));
    updated.whtr = whtr;
    updated.whtrRisk = whtr > 0.5;
  }

  // 3. Reconciliación de Clasificación de Riesgo Epidemiológico desde GCP
  if (onboardingState?.riskClassification) {
    updated.fhirRiskClassification = onboardingState.riskClassification;
  }
  if (onboardingState?.fhirReconciled !== undefined) {
    updated.fhirReconciled = onboardingState.fhirReconciled;
  }

  // 4. Sellos de Reconciliación Clínica
  updated.lastFhirSyncAt = new Date().toISOString();
  updated.fhirSyncStatus = hasPendingOutbox ? "PENDING_FHIR" : "SYNCED";

  return { reconciledProfile: updated, hasUpdates };
}

